import {MapService, gmAPI} from '@/services/MapService';
import {Graphic} from './Graphic';
import {MetricService} from '@/services/MetricService.js';
import {findPixelInImage} from '@/services/GraphicService/colorUtilities';
import {EventService} from '@/services/EventService';
import {getStore} from '@/services/store';
import {MENU_BAR_WIDTH_PIXELS} from '@/constants/MainMenu';
import {TokenService} from '../TokenService';
import {replacer} from '@/lib/strings';
import {MapLayer} from './MapLayer';
import {createFiberContentData} from '@/lib/classes/InfoWindowContent/helpers/wmtsTile';
import {SubscriptionManager} from '@northpoint-development/labs-common-core';
import {SubscriptionEvent, SubscriptionResult} from '@/lib/filters/subscriptions';
import {InfoWindowContent} from '@/lib/classes/InfoWindowContent';

const TILE_PLACEMENTS = {
  'A': {x: -1, y: -1},
  'B': {x: 0, y: -1},
  'C': {x: 1, y: -1},
  'D': {x: -1, y: 0},
  // Origin tile
  'E': {x: 0, y: 0},
  'F': {x: 1, y: 0},
  'G': {x: -1, y: 1},
  'H': {x: 0, y: 1},
  'I': {x: 1, y: 1},
};

interface WMTSTileGraphicData extends MapLayer {
  currentMonthUsage?: number;
}

interface Provider {
  common_name: string;
}

// TODO: extend the token service to return an object reference instead of a primitive.
// We can then always be grabbing a refreshed token from the service.
let token;

const incrementUsage = (instance) => {
  // TODO: this should be configured based on the usageMetricName, not a hardcoded string
  MetricService.increaseMetric(instance.usageMetricName);
  instance.currentMonthUsage++;
};

async function createWMTSTileGraphic(layer: MapLayer): Promise<WMTSTileGraphic> {
  if (layer.isPublicLayer) {
    return new WMTSTileGraphic(layer);
  }
  if (!layer.tokenName) {
    throw new Error(`Private layer ${layer.name} requires a token, but no tokenName was provided.`);
  }
  try {
    token = await TokenService.getToken(layer.tokenName);
  } catch (error) {
    error.message = `Failed to get ${layer.tokenName}: ${error.message}`;
    console.error(error);
  }
  const currentMonthUsage = await MetricService.getTileUsage(layer.usageMetricName);
  return new WMTSTileGraphic({...layer, currentMonthUsage} as WMTSTileGraphicData);
}

// Given an event and regex, checks if the clicked tile is opaque.
async function isClickedTileOpaque(event: google.maps.MapMouseEvent, regex: RegExp): Promise<boolean> {
  // "point" correctly represents the click location, the gmAPI event.pixel does not take into account the map's
  // position within the window
  const mouseEvent = event.domEvent as MouseEvent;
  const point = {x: mouseEvent.x, y: mouseEvent.y};

  const clickRadius = 7; // pixels around the hand cursor to search for opaque pixels

  const searchImgs = MapService.tilesFromPoint(
    {x: point.x, y: point.y},
    clickRadius,
    (element: HTMLImageElement) => !!element?.src.match(regex),
  );

  // check the alpha value of the provided pixel to determine if it is any percent opaque
  const pixelMatcher = (/** @type {ImageData} */ pixel) => pixel.data[3] > 0 ? pixel.data : null;
  let foundOpaquePixel = null;
  for (let i = 0; i < searchImgs.length; i++) {
    const img = searchImgs[i];
    const newX = point.x + (TILE_PLACEMENTS[img.placement].x * clickRadius);
    const newY = point.y + (TILE_PLACEMENTS[img.placement].y * clickRadius);
    const newLatLng = MapService.screenPixelToLatLng(newX, newY) as google.maps.LatLng;
    const tilePoint = MapService.fromClickEventToPoint({
      latLng: newLatLng,
      pixel: {
        // fromLatLngToTilePoint doesn't take into account the map's position within the window
        x: newX - MENU_BAR_WIDTH_PIXELS,
        y: newY,
      },
    });
    try {
      foundOpaquePixel = await findPixelInImage(img.el, {
        x: tilePoint.x,
        y: tilePoint.y,
      }, clickRadius, pixelMatcher);
    } catch (error) {
      console.error(`Could not find opaque pixel in image: ${error.message}`);
      getStore().commit('setSnack', {message: 'Image failed to load. Please refresh the page and try again.', color: 'error'});
    }
    if (foundOpaquePixel) break;
  }

  return foundOpaquePixel;
}

class WMTSTileGraphic extends Graphic {
  layerName: string; // The name of the layer to pass to the Fiber Locator API call.
  currentMonthUsage: number; // The current month's usage of fiber tiles
  urlMap: Record<string, string>; // The relevant wmts urls
  render: boolean;
  clickable: boolean;
  customClickHandler: string; // The name of the custom click handler to use for this graphic
  usageMetricName: string; // The name of the metric to use for usage tracking
  isPublicLayer: boolean; // Indicates whether the layer is public or requires a token
  subscriptions: SubscriptionManager; // The subscription manager for this graphic
  tileCache: Set<string>; // Cache to track which tiles have already been requested

  constructor(data: WMTSTileGraphicData) {
    super();
    this.layerName = data.name;
    this.currentMonthUsage = data.currentMonthUsage ?? 0;
    this.urlMap = data.urlMap;
    this.render = false;
    this.clickable = false;
    this.customClickHandler = data.customClickHandler;
    this.usageMetricName = data.usageMetricName;
    this.isPublicLayer = data.isPublicLayer;
    this.subscriptions = new SubscriptionManager();
    this.tileCache = new Set(); // Initialize the tile cache

    this.addHandler('click', `wmts-${data.id}-click`, async (event) => {
      const subjects = await this.handleTileClick(event);
      if (!subjects?.length) return;
      // get the customFieldMapping from the layer options
      const customFieldMapping = data.options[0]?.customFieldMapping;
      let content;
      if (customFieldMapping === 'fiber') {
        content = new InfoWindowContent(await createFiberContentData(data.id, subjects));
      } else {
        console.warn(`Custom field mapping '${customFieldMapping}' is not implemented.`);
      }
      this.subscriptions.publish('open_info_window', content);
    });
  }

  // Adds a handler to the layer for the specified event.
  addHandler(event: string, handlerName: string, handler) {
    // Wrap the handler in logic to suppress the listener if this graphic is not clickable.
    const wrapped = (event !== 'click') ? handler : (...args) => {
      if (this.clickable && this.render) {
        return handler(...args);
      }
    };
    EventService.addLayerHandler(this.layerName, handlerName, wrapped);
  }

  // A customClickHandler for fiber clicks
  async handleFiberClick(event: google.maps.MapMouseEvent): Promise<string[]> {
    // If the click did not land on an opaque pixel, do not make request to the API for fiber providers to save $$$.
    const isOpaque = await isClickedTileOpaque(event, new RegExp(this.urlMap.tileImgSrc, 'g'));
    if (!isOpaque) {
      return null;
    }
    MapService.setCursor('wait');
    try {
      // Adjust the search radius based on the zoom level, to influence accuracy of the search
      const radius = Math.pow(2, 21 - MapService.getMap().getZoom());
      const response = await fetch(
        replacer(this.urlMap.wmtsInfoNear, {lng: event.latLng.lng(), lat: event.latLng.lat(), radius}),
        {
          method: 'GET',
          headers: {
            token,
            Accept: 'application/json',
          },
          signal: AbortSignal.timeout(10000),
        },
      );
      incrementUsage(this);

      const json = await response.json();
      const providers: Provider[] = Object.values(json.result);
      if (json.status !== 'ok') {
        throw new Error(json.message);
      }
      const validResults = providers.filter((provider) => provider?.common_name);
      if (!validResults.length) {
        getStore().commit('setSnack', {message: 'No Fiber providers found. Try clicking another location.', color: 'error'});
      }
      return validResults.map((provider) => provider.common_name);
    } catch (error) {
      console.error(`Could not handle Fiber click: ${error.message}`);
      let snackMsg = error.message;
      if (error.name === 'TimeoutError') {
        snackMsg = 'The Fiber Locator API request timed out. Please try again.';
      }
      getStore().commit('setSnack', {message: snackMsg, color: 'error'});
    } finally {
      MapService.setCursor('default');
    }
  }

  // Handles a tile click event on the map.
  async handleTileClick(event: google.maps.MapMouseEvent): Promise<string[]|null> {
    if (!this.customClickHandler) {
      console.warn(`No custom click handler defined for WMTS Tile Graphic '${this.layerName}'.`);
      return null;
    }
    if (this.customClickHandler === 'fiber') {
      return await this.handleFiberClick(event);
    } else {
      console.warn(`Custom click handler '${this.customClickHandler}' is not implemented.`);
      return null;
    }
  }

  // Sets whether this graphic can be clicked.
  setClickable(clickable: boolean) {
    this.clickable = clickable;
  }

  // Attaches the graphic to a map or removes it from a map.
  setMap(map: google.maps.Map|null) {
    if (!map) {
      const overlayMapTypes = MapService.getMap().overlayMapTypes;
      overlayMapTypes.forEach((overlayMapType, i) => {
        if (overlayMapType?.name === this.layerName) {
          overlayMapTypes.removeAt(i);
          this.setClickable(false);
          this.render = false;
        }
      });
    } else {
      this.createLayer();
    }
  }

  // Creates a new ImageMapType for the WMTS tile graphic.
  createLayer() {
    let layerIndex = -1;
    const overlayMapTypes = MapService.getMap().overlayMapTypes;
    overlayMapTypes.forEach((overlayMapType, index) => {
      if (overlayMapType?.name === this.layerName) {
        layerIndex = index;
      }
    });
    const newLayer = new gmAPI.ImageMapType({
      getTileUrl: (coord, zoom) => {
        if (token) {
          // Check if we have already requested this tile
          const cacheKey = `${zoom}-${coord.x}-${coord.y}`;
          const isNewTile = !this.tileCache.has(cacheKey);

          if (isNewTile) {
            incrementUsage(this);
            this.tileCache.add(cacheKey);

            // Periodic cleanup: clear the cache every 5000 entries to prevent unbounded growth
            if (this.tileCache.size > 5000) {
              this.tileCache.clear();
            }
          }

          return replacer(this.urlMap.wmtsTileUrl, {token, zoom, x: coord.x, y: coord.y});
        }
        return replacer(this.urlMap.wmtsTileUrl, {zoom, x: coord.x, y: coord.y});
      },
      tileSize: new gmAPI.Size(256, 256),
      name: this.layerName,
    });
    // Push a new layer if it hasn't been created yet, otherwise replace the existing one
    if (layerIndex < 0) {
      overlayMapTypes.push(newLayer);
    } else {
      overlayMapTypes.setAt(layerIndex, newLayer);
    }
    this.render = true;
  }

  subscribe(eventName: string, callback: (event: SubscriptionEvent) => void): SubscriptionResult {
    return this.subscriptions.addSubscription(eventName, callback);
  }
}

export {
  createWMTSTileGraphic,
  WMTSTileGraphic,

  // testing
  isClickedTileOpaque,
};
